// @givmo/app-admin — §317 gift-card disclosure: the compiled-in DEGRADED-PATH floor (EP-WEB4).
//
// The AB 488 §317 pre-purchase disclosure is server-driven from the backend disclaimer registry
// (`GET /disclaimers/resolve?surface=GIFT_CARD_PURCHASE`). ★ THAT ROW IS LIVE AND RESOLVES 200 —
// this floor is the FALLBACK, not the normal render path. Treat a floor render as DEGRADED. The floor
// bytes are BYTE-IDENTICAL to the donate app's `apps/donate/app/lib/gift-disclaimer.ts`, and the
// pinned hash equals the backend's `compute_content_hash` of the six items joined by a single newline.
//
// ★ COUNSEL-PINNED LAW — GIFT_CARD_PURCHASE **v2**, President-signed 2026-08-13 (`givmo-files`
// `legal/disclosures/gift-card-purchase-disclaimers-2026-08.md` @ 36fc4a9). Copy the bytes verbatim —
// never reword. v2 supersedes the v1 copy whose items 1 and 5 asserted an unconditional charitable
// deduction (wrong for the non-itemizer post-OBBBA under IRC §170(p)(2), unconditioned for the
// itemizer); item 2 now also carries the non-cashability disclosure. The "Terms" heading is
// client-owned and is NOT part of the floor text.
//
// ⚠ SHARED-SURFACE NON-CURE, and it bites HERE specifically. The employer surface funds a card either
// by charging a company card or by debiting the company wallet. This one registry row serves both, and
// v2's items 1 and 5 are written for the CARD purchaser — on the company-WALLET path they remain
// materially false, because the deductible contribution happened when the wallet was funded, not here.
// The cure is a separate `GIFT_CARD_PURCHASE_WALLET` row (backend enum label exists, seedless); until
// it is seeded and set effective, the wallet path is knowingly uncured. Do not "fix" it by reworking
// these bytes — that would break the signed hash and the card path with it.

/**
 * The six §317 disclosure items, IN ORDER, in donation-first language — the President-signed v2 copy.
 * Byte-identical to the donate app's floor. Rendered in the terms panel above the send button, one
 * line per item.
 */
export const GIFT_DISCLAIMER_FLOOR_LINES: readonly string[] = [
  "Your payment is an irrevocable donation to Givmo Charitable Fund (a 501(c)(3) donor-advised-fund sponsor, EIN 99-2418877) — not to the recipient's eventual charity.",
  "A charity receives funds only after your recipient redeems the gift and recommends a grant. The gift's value can be used only to recommend grants to charities — it is not redeemable for cash or for any merchant's goods or services. If it's never redeemed, no charity receives funds; after about three years the Fund may apply unredeemed gifts to its grantmaking, while always honoring presented gifts.",
  "Timing depends on redemption (there's no deadline). Once redeemed and a grant is recommended, the Fund's normal disbursement schedule applies.",
  "No fees are ever deducted from the gift's value. Card processing is added at checkout and itemized above.",
  "For federal income tax purposes, you can deduct this donation — in the tax year you buy the gift — only if you itemize; gifts to donor-advised funds aren't eligible for the deduction available to people who don't itemize, and itemized charitable deductions count only to the extent your total giving for the year is more than 0.5% of your contribution base (generally your adjusted gross income). What you can actually deduct depends on your circumstances, so keep your Givmo Charitable Fund receipt and consult a tax professional. Redeeming a gift, and donations made by redeeming, are not tax-deductible.",
  'Charity selections are recommendations to Givmo Charitable Fund, which retains exclusive legal control over all grants.',
];

/**
 * The floor `content_hash` — the sha256 of the six items joined with a single `\n` after the backend's
 * normalization. Equals the v2 registry row's hash (backend migrations `bad1b72a3a56` seed +
 * `510b318974d1` set-effective); `gift-disclaimer.test.ts` recomputes it from the lines above, so any
 * reword changes the hash and reddens the test.
 */
export const GIFT_DISCLAIMER_FLOOR_HASH =
  '48b5b4a2c1b53d28adcadca7f19d90e5bcb4fbb3a55642a68149c793d9a35010';

/** The disclaimer surface key the backend registry keys on for the gift-card purchase block. */
export const GIFT_DISCLAIMER_SURFACE = 'GIFT_CARD_PURCHASE';

/**
 * Resolve the §317 disclosure lines from a `GET /disclaimers/resolve` response, falling back to the
 * compiled-in floor. Mirrors the donate resolver's semantics (client-side here, since the admin app
 * is a SPA): the server `content_plaintext` split on `\n` (blank lines dropped) on a 200 with a
 * non-empty body, else the floor — so `undefined` (pending / any error) OR an empty/whitespace body
 * yields the floor. NEVER throws; the terms panel always renders the correct bytes. The row is
 * LIVE, so the 200 branch is the normal path and a floor render means something failed.
 */
export function resolveGiftDisclaimerLines(
  data: { content_plaintext?: string | null } | undefined,
): readonly string[] {
  const plaintext = data?.content_plaintext;
  if (typeof plaintext !== 'string') return GIFT_DISCLAIMER_FLOOR_LINES;
  const lines = plaintext
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  return lines.length > 0 ? lines : GIFT_DISCLAIMER_FLOOR_LINES;
}
